import { useTranslation } from 'react-i18next';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { FreeNode } from './FreeNode';
import { FREE_POOL_ID } from './dropResolution';
import type { EditorNode } from './types';
import styles from '../InterventionGraphEditor.module.css';
import { PlacementRuleType } from '../../../api/generated';

interface FreePoolProps {
  free: EditorNode[];
  allNodes: EditorNode[];
  strokeColor: string;
  fillColor: string;
  getWiName: (wiId: string) => string;
  getWiLabel: (wiId: string) => string;
  onRemove: (id: string) => void;
  onRemoveDep: (wiId: string, depId: string) => void;
  onAddDep: (wiId: string, type: PlacementRuleType, targetId?: string) => void;
  onEditAllowedDays: (wiId: string, rect: DOMRect) => void;
}

export const FreePool = ({
  free,
  allNodes,
  strokeColor,
  fillColor,
  getWiName,
  getWiLabel,
  onRemove,
  onRemoveDep,
  onAddDep,
  onEditAllowedDays
}: FreePoolProps) => {
  const { t } = useTranslation();
  const { setNodeRef, isOver } = useDroppable({ id: FREE_POOL_ID });

  return (
    <div ref={setNodeRef} className={`${styles.freePool} ${isOver ? styles.freePoolOver : ''}`}>
      <span className={styles.freePoolLabel}>{t('config-free-interventions')}</span>
      <SortableContext items={free.map((wi) => wi.id)} strategy={horizontalListSortingStrategy}>
        <div className={styles.freeList}>
          {free.length === 0 && (
            <span className={styles.freeEmptyHint}>{t('config-free-empty-hint')}</span>
          )}
          {free.map((wi) => (
            <FreeNode
              key={wi.id}
              wi={wi}
              strokeColor={strokeColor}
              fillColor={fillColor}
              allWeekInterventions={allNodes}
              getWiName={getWiName}
              getWiLabel={getWiLabel}
              onRemove={() => onRemove(wi.id)}
              onRemoveDep={(depId) => onRemoveDep(wi.id, depId)}
              onAddDep={(type, targetId) => onAddDep(wi.id, type, targetId)}
              onEditAllowedDays={(rect) => onEditAllowedDays(wi.id, rect)}
            />
          ))}
        </div>
      </SortableContext>
    </div>
  );
};
